import React from 'react';
import { OrderShipment } from '@jbhi-fi/apis/src/orderTrackingThemeApi/types';
import { WhatsComing } from '../WhatsComing';
import { styles } from './TrackingResult.css';
import { TrackingResultHeader } from './TrackingResultHeader';
import { ProductInformationProps } from '../ProductInformation/ProductInformationCard';
import {
  CUSTOMER_COLLECT_SHIPMENT_PROGRESSION,
  DELIVERY_SHIPMENT_PROGRESSION,
  MARKETPLACE_SHIPMENT_PROGRESSION,
  UBER_SHIPMENT_PROGRESSION,
  UNTRACKED_SHIPMENT_PROGRESSION,
} from '../constants';
import { TrackingResultContent } from './TrackingResultContent';
import { ShipmentProgress } from '../ShipmentProgress';
import { MarkdownBlock } from '../../MarkdownBlock';
import { TrackingProvider } from '../../Tracking';
import { OrderShipmentTransformer } from '../transformers/OrderShipmentTransformer';
import { BackOrderMessaging } from '../BackOrderMessaging/BackOrderMessaging';
export interface TrackingResultProps {
  orderNumber: string;
  title: string;
  subtitle?: string;
  shipment: OrderShipment;
  lineItems: Array<ProductInformationProps>;
  renderingOrder?: Array<'fulfilment' | 'history' | 'whatsComing'>;
}

export const TrackingResult = ({
  orderNumber,
  title,
  subtitle,
  shipment,
  lineItems,
  renderingOrder,
}: TrackingResultProps) => {
  const isOrderComplete = shipment.status === 'Delivered' || shipment.status === 'Collected';
  const hasHeader = shipment.type !== 'DigitalDelivery';
  const isBackOrder = shipment.isBackOrder;

  const getStatusProgression = () => {
    if (shipment.type === 'CustomerCollect') return CUSTOMER_COLLECT_SHIPMENT_PROGRESSION;
    if (shipment.messaging?.carrier?.name.includes('Uber')) return UBER_SHIPMENT_PROGRESSION;
    if (shipment.isMarketplace) return MARKETPLACE_SHIPMENT_PROGRESSION;
    return shipment.isTracked ? DELIVERY_SHIPMENT_PROGRESSION : UNTRACKED_SHIPMENT_PROGRESSION;
  };

  if (
    shipment.type === 'DigitalDelivery' ||
    (shipment.type == 'BulkyDelivery' && !isBackOrder && shipment.messaging?.template.importantText)
  ) {
    return <NonTrackedResult orderNumber={orderNumber} lineItems={lineItems} shipment={shipment} />;
  }

  if (shipment.status === 'Cancelled') {
    return <CancelledTrackingResult orderNumber={orderNumber} lineItems={lineItems} />;
  }

  if (isBackOrder) {
    return <BackOrderNoDate orderNumber={orderNumber} lineItems={lineItems} />;
  }

  return (
    <TrackingProvider category="tracking-result" shouldInheritFromParentContexts>
      <div data-testid="order-tracking-result">
        {hasHeader ? (
          <TrackingResultHeader
            title={title}
            subtitle={subtitle}
            isOrderComplete={isOrderComplete}
            status={shipment.history?.[0]?.status || OrderShipmentTransformer.toFriendlyShipmentStatus(shipment.status)}
            statusUpdatedAt={shipment.statusUpdatedAt}
            alert={shipment.messaging?.template.headerAlert}
            isTracked={shipment.isTracked}
          />
        ) : null}
        <div className={styles.spacer({ completed: isOrderComplete, hasHeader })}></div>
        <div className={styles.pageContents}>
          {shipment.type !== 'GiftCardPhysical' ? (
            <div className={styles.progressBar}>
              <ShipmentProgress
                variant={shipment.type === 'CustomerCollect' ? 'customerCollect' : 'delivery'}
                steps={getStatusProgression()}
                shipmentStatus={
                  shipment.status === 'Failed'
                    ? // In case of failure, use last known status before failed status
                      OrderShipmentTransformer.toProgressStatus(shipment.history?.[1].shipmentStatus || 'Shipped')
                    : shipment.progressStatus
                }
                hasWarning={shipment.status === 'Failed'}
              />
            </div>
          ) : null}
          <TrackingResultContent
            orderNumber={orderNumber}
            renderingOrder={renderingOrder}
            shipment={shipment}
            lineItems={lineItems}
          />
        </div>
      </div>
    </TrackingProvider>
  );
};

export type CancelledTrackingResultProps = {
  orderNumber: string;
  lineItems: Array<ProductInformationProps>;
};

export const CancelledTrackingResult = ({ orderNumber, lineItems }: CancelledTrackingResultProps) => {
  return (
    <div data-testid="order-tracking-result" className={styles.contentSection}>
      <div className={styles.spacer({ hasHeader: false })}></div>
      <div className={styles.pageContents}>
        <WhatsComing status="Cancelled" orderNumber={orderNumber} lineItems={lineItems} />
      </div>
    </div>
  );
};

export type DigitalTrackingResultProps = {
  orderNumber: string;
  shipment: OrderShipment;
  lineItems: Array<ProductInformationProps>;
};

export const NonTrackedResult = ({ orderNumber, shipment, lineItems }: DigitalTrackingResultProps) => {
  return (
    <>
      <div className={styles.spacer({ hasHeader: false })} />
      <div className={styles.pageContents}>
        <div className={styles.contentSection}>
          <div role="paragraph" className={styles.digitalMessagingContainer}>
            {shipment.messaging?.template.importantText?.heading ? (
              <div className={styles.digitalMessagingHeader}>{shipment.messaging.template.importantText.heading}</div>
            ) : null}
            {shipment.messaging?.template.importantText?.content ? (
              <div role="paragraph">
                <MarkdownBlock shouldLinkOpenInNewTab>
                  {shipment.messaging.template.importantText.content}
                </MarkdownBlock>
              </div>
            ) : null}
          </div>
        </div>
        <div className={styles.contentSection}>
          <WhatsComing orderNumber={orderNumber || shipment.id} status={shipment.status} lineItems={lineItems} />
        </div>
      </div>
    </>
  );
};

export const BackOrderNoDate = ({ orderNumber, lineItems }: CancelledTrackingResultProps) => {
  return (
    <>
      <div data-testid="order-tracking-result" className={styles.contentSection}>
        <div className={styles.spacer({ hasHeader: false })}></div>
        <div className={styles.pageContents}>
          <BackOrderMessaging />
        </div>
      </div>
      <div data-testid="order-tracking-result" className={styles.contentSection}>
        <div className={styles.spacer({ hasHeader: false })}></div>
        <div className={styles.pageContents}>
          <WhatsComing status="Confirmed" orderNumber={orderNumber} lineItems={lineItems} />
        </div>
      </div>
    </>
  );
};
